import React from 'react';
import { Download, FileDown, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Period-picker dialog for the audit-log CSV export. Parent owns the
 * period/year/quarter/month state + the actual blob-download handler;
 * this component is purely the JSX shell.
 */
export function AuditLogExportDialog({
  open, onOpenChange,
  period, setPeriod,
  yearVal, setYearVal,
  quarterVal, setQuarterVal,
  monthVal, setMonthVal,
  yearOptions,
  exporting,
  onExport,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !exporting) onOpenChange(false); }}>
      <DialogContent className="max-w-md" data-testid="audit-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-amber-500" /> Export audit logs
          </DialogTitle>
          <DialogDescription>
            Download impersonation history as a CSV for compliance or security review. Pick a calendar window — the file includes every audit field (admin, target, purpose, IP, user-agent, token JTI, expiry).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Period</label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="audit-export-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yearly">Yearly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Year</label>
              <Select value={yearVal} onValueChange={setYearVal}>
                <SelectTrigger data-testid="audit-export-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            {period === 'quarterly' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Quarter</label>
                <Select value={quarterVal} onValueChange={setQuarterVal}>
                  <SelectTrigger data-testid="audit-export-quarter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Q1 (Jan – Mar)</SelectItem>
                    <SelectItem value="2">Q2 (Apr – Jun)</SelectItem>
                    <SelectItem value="3">Q3 (Jul – Sep)</SelectItem>
                    <SelectItem value="4">Q4 (Oct – Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {period === 'monthly' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Month</label>
                <Select value={monthVal} onValueChange={setMonthVal}>
                  <SelectTrigger data-testid="audit-export-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((name, idx) => (
                      <SelectItem key={name} value={String(idx + 1)}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 text-[11px] text-slate-600">
            File: <span className="font-mono text-slate-800">
              {`user-impersonation-audit_${yearVal}`}
              {period === 'quarterly' && `-Q${quarterVal}`}
              {period === 'monthly' && `-${String(monthVal).padStart(2, '0')}`}
              .csv
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting} data-testid="audit-export-cancel">
            Cancel
          </Button>
          <Button
            onClick={onExport}
            disabled={exporting}
            data-testid="audit-export-confirm"
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AuditLogExportDialog;
